import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { StrHlp } from 'src/lib/fliko/helpers/string.helper';
import { EstateType } from 'src/lib/fliko/models/estate/estate.data';
import { ResidentType } from 'src/lib/fliko/models/person/resident.data';
import { RentalUnitType } from 'src/lib/fliko/models/units/unit.data';
import { GrammarHelper } from 'src/app/shared/helpers/grammar.helper';
import { SelectInputHelper } from 'src/app/shared/helpers/select-input.helper';
import { UnitFilterDictionary } from 'src/app/shared/models/unit-filter-dictionary.model';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { SortHelper } from '../../../helpers/sort.helper';
import { SelectInputData } from '../../../interfaces/select-input-data.interface';
import { SortCriteria, SortOrder } from '../../../models/sort-criteria';
import { LocalizationService } from '../../../services/localization.service';
import { ReceiverSelectFilters } from '../view-models/receivers-select.view-model';

@Component({
  selector: 'app-receivers-select-filters[allData]',
  templateUrl: './receivers-select-filters.component.html',
  styleUrls: ['./receivers-select-filters.component.scss']
})
export class ReceiversSelectFiltersComponent implements OnInit {
  @Input() allData!: UnitFilterDictionary;
  @Input() initData!: ReceiverSelectFilters;
  @Output() onSelectChanged = new EventEmitter<ReceiverSelectFilters>();

  public get dropdownSettings(): IDropdownSettings {
    return {
      singleSelection: false,
      idField: 'value',
      textField: 'title',
      selectAllText: this.ls.translate('select.selectAll'),
      unSelectAllText: this.ls.translate('select.unselectAll'),
      itemsShowLimit: 5
    };
  }

  public residentTypes: SelectInputData<ResidentType>[] = [];
  public residentTypesSelected: SelectInputData<ResidentType>[] = [];
  public unitTypes: SelectInputData<RentalUnitType>[] = [];
  public unitTypesSelected: SelectInputData<RentalUnitType>[] = [];
  public filtered: UnitFilterDictionary = UnitFilterDictionary.empty();
  public selected: UnitFilterDictionary = UnitFilterDictionary.empty();

  private readonly SORT_CRITERIA: SortCriteria = { param: 'title', order: SortOrder.ASC, separator: '/' };
  private optionsSorter = SortHelper.getComp<SelectInputData<string>>(this.SORT_CRITERIA);

  constructor(
    private ls: LocalizationService,
    private ctxDS: ContextDataService
  ) {
    const trCap = (x: string) => StrHlp.capitalize(this.ls.translate(x));

    const estateType = this.ctxDS.peekCurrentEstate()?.type ?? EstateType.COMMUNITY;
    const mapRes = (x: ResidentType) => trCap(GrammarHelper.getResidentKeyInCase(estateType, x, 'M', true));
    const mapUnitType = (x: RentalUnitType) => trCap(GrammarHelper.getUnitKeyInCase(estateType, x, 'M'));

    this.residentTypes = SelectInputHelper.fromValues([ResidentType.LANDLORD, ResidentType.TENANT], mapRes);
    this.residentTypesSelected = this.residentTypes;
    this.unitTypes = SelectInputHelper.fromValues(Object.values(RentalUnitType), mapUnitType);
    this.unitTypesSelected = this.unitTypes;
  }

  ngOnInit(): void {
    if (this.allData) this.allData.buildings.sort(this.optionsSorter);

    if (this.initData) {
      this.residentTypesSelected = this.residentTypes.filter((x) => this.initData.types.includes(x.value));
      this.unitTypesSelected = this.unitTypes.filter((x) => this.initData.unitTypes.includes(x.value));
      this.initData.buildings.forEach((b) => this.toggleBuilding(b, true));
      this.selected.buildings = this.initData.buildings;
      this.initData.staircases.forEach((s) => this.toggleStaircase(s, true));
      this.selected.staircases = this.initData.staircases;
      this.selected.numbers = this.initData.numbers;
      this.toggleNumbers();
    }
  }

  public toggleAllBuildings(select: boolean): void {
    if (select) {
      // ? Select ALL buildings
      this.filtered.staircases = this.allData.staircases.sort(this.optionsSorter);
    } else {
      // ? Deselect ALL buildings
      this.filtered = UnitFilterDictionary.empty();
      this.selected = UnitFilterDictionary.empty();
    }
    this.onSelectChanged.emit(this.prepareEmit());
  }

  // HACK: any = SelectInputData,
  // but in definition ng-multiselect-dropdown function returns ListItem, which is incorrect, but compiler is screaming ;c
  public toggleBuilding(bNum: any, select: boolean): void {
    this.updateLesserFilters('staircases', (bNum.value ?? '').toString(), select);
    if (!select) this.updateLesserFilters('numbers', (bNum.value ?? '').toString(), select);
    this.onSelectChanged.emit(this.prepareEmit());
  }

  public toggleAllStaircases(select: boolean): void {
    if (select) {
      this.filtered.numbers = this.allData.numbers.sort(this.optionsSorter);
    } else {
      this.filtered.numbers = [];
      this.selected.numbers = [];
    }
    this.onSelectChanged.emit(this.prepareEmit());
  }

  // HACK: the same case as in toggleBuilding
  public toggleStaircase(sNum: any, select: boolean): void {
    this.updateLesserFilters('numbers', (sNum.value ?? '').toString(), select);
    this.onSelectChanged.emit(this.prepareEmit());
  }

  public toggleResidentType(select: boolean, all: boolean, type?: any) {
    if (all) {
      this.residentTypesSelected = select ? this.residentTypes : [];
    } else {
      if (select) this.residentTypesSelected.push(type);
      else this.residentTypesSelected = this.residentTypesSelected.filter((x) => x.value != type.value);
    }
    this.onSelectChanged.emit(this.prepareEmit());
  }

  public toggleUnitType(select: boolean, all: boolean, type?: any) {
    if (all) {
      this.unitTypesSelected = select ? this.unitTypes : [];
    } else {
      if (select) this.unitTypesSelected.push(type);
      else this.unitTypesSelected = this.unitTypesSelected.filter((x) => x.value != type.value);
    }
    this.onSelectChanged.emit(this.prepareEmit());
  }

  public toggleNumbers(): void {
    this.onSelectChanged.emit(this.prepareEmit());
  }

  /**
   * Update staircases on building select / deselect or numbers on staircase select / deselect
   * @param {'staircases' | 'numbers'} collection ReceiverSelectData collection
   * @param {string}                   key
   * @param {boolean}                  select     is this action on building / staircase select or deselect
   */
  private updateLesserFilters(collection: 'staircases' | 'numbers', key: string, select: boolean) {
    const keyFilter = (x: SelectInputData<string>) => select == !!`|${x.value}`.includes(`|${key}|`);
    if (select) {
      const newValues = this.allData[collection].filter(keyFilter);
      this.filtered[collection] = [...newValues, ...this.filtered[collection]].sort(this.optionsSorter);
    } else {
      this.filtered[collection] = this.filtered[collection].filter(keyFilter).sort(this.optionsSorter);
      this.selected[collection] = this.selected[collection]
        .filter((x) => this.filtered[collection].filter((y) => y.value == x.value))
        .sort(this.optionsSorter);
    }
  }

  private prepareEmit(): ReceiverSelectFilters {
    return {
      ...this.selected,
      types: this.residentTypesSelected.map((x) => x.value),
      unitTypes: this.unitTypesSelected.map((x) => x.value)
    };
  }
}
